/**
 * Keeps OrbitControls' idea of how many fingers are down in step with reality (M115), carried over from Sable Reserve's
 * `src/render/pointerHygiene.ts` (at `0bb78b3`; its entry "The map went two-finger-only while one finger was on the
 * glass"), its reasoning and its code unchanged but for this game's two controls on the one canvas (below).
 *
 * The user's report here: "its doing a zoom thing on phone where i cant move the camera and it keeps zooming in and out
 * even if i use 1 finger", and of Sable Reserve's: "the screen thought that i had two fingers pressing when it was only
 * one finger, causing the one finger to zoom".
 *
 * OrbitControls learns a pointer has lifted only by receiving that pointer's `pointerup` or `pointercancel`. When one
 * of those never arrives (iOS Safari drops them when a system gesture takes over mid-touch, and a backgrounded tab can
 * do it too), the id stays in its list for the life of the page. With one phantom pointer held, the next single finger
 * makes two, so every drag is read as a pinch and the camera dollies: sticky, not momentary. MEASURED in
 * `tests/pointerHygiene.test.ts` against the real OrbitControls: one dropped `pointerup` turns a one-finger drag from a
 * pure turn into a change of the camera's distance.
 *
 * The signal that lets this be fixed honestly is `isPrimary`. The browser sets it on a `pointerdown` only when no other
 * pointer of that type is down, its own bookkeeping, which is not the thing that went wrong. So a primary touch is proof
 * that every pointer of that type the controls still hold is a phantom, and can be retired: by dispatching the
 * `pointerup` that never came, rather than reaching into the controls, so that all of OrbitControls' own cleanup runs
 * (its state reset, its move listeners dropped, its end event fired).
 *
 * The sweep listens on an ancestor, the window, in the capturing phase, so the phantom is retired before OrbitControls
 * counts the new finger as a second one (a capturing listener on the canvas itself would run in registration order,
 * after OrbitControls; Sable Reserve's sabotage found it recovers even so, on the same event, but the wrong state is
 * then entered and left, with a spurious start and end).
 *
 * This game has two OrbitControls on its canvas, the whole tank's (`scene.ts`) and the habitat scenes' (`HabitatHost`),
 * one enabled at a time. A dispatched `pointerup` reaches both, and each drops the id only if it holds it, so the one
 * sweep serves both, and the pinch's aim (`scene.ts`, `host.ts`) forgets the phantom by the same event.
 */

/**
 * Attaches the reconciliation to the element the controls listen on; returns a function that detaches it. `root` is
 * the ancestor the sweep captures on, so a test can drive the capturing phase; in the game it is always the window.
 */
export function keepPointersHonest(element: HTMLElement, root: EventTarget = window): () => void {
  /** Pointers the controls have been told about and not told to release, by type, as `isPrimary` is per type. */
  const held = new Map<number, string>();
  const onDown = (event: Event): void => {
    const pointer = event as PointerEvent;
    // Only pointers that land on the canvas: those are the ones the controls are given, and the only ones they can be
    // left holding.
    if (event.target !== element) return;
    if (pointer.isPrimary) retirePhantoms(element, held, pointer);
    held.set(pointer.pointerId, pointer.pointerType);
  };
  // Mirrors the controls' own bookkeeping, which drops a pointer when its up reaches the canvas, so on the element.
  const onUp = (event: Event): void => {
    held.delete((event as PointerEvent).pointerId);
  };
  root.addEventListener('pointerdown', onDown, { capture: true });
  element.addEventListener('pointerup', onUp);
  element.addEventListener('pointercancel', onUp);
  return () => {
    root.removeEventListener('pointerdown', onDown, { capture: true });
    element.removeEventListener('pointerup', onUp);
    element.removeEventListener('pointercancel', onUp);
  };
}

function retirePhantoms(element: HTMLElement, held: Map<number, string>, event: PointerEvent): void {
  const stale = [...held].filter(([id, type]) => type === event.pointerType && id !== event.pointerId);
  if (stale.length === 0) return;
  // `releasePointerCapture` throws NotFoundError for a pointer the browser no longer considers active, which is
  // precisely what a phantom is. OrbitControls calls it when its last pointer lifts, so it is stubbed for the length of
  // the sweep rather than left to throw out of a listener and abandon the rest of the cleanup.
  const release = element.releasePointerCapture.bind(element);
  element.releasePointerCapture = () => {};
  try {
    for (const [id, type] of stale) {
      held.delete(id);
      element.dispatchEvent(new PointerEvent('pointerup', { pointerId: id, pointerType: type, bubbles: true, cancelable: false }));
    }
  } finally {
    element.releasePointerCapture = release;
  }
}
